(function (global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Carousel = global.NewHomeSource.Carousel || {};

  if (!global.NewHomeSource.Carousel.EventStore) {
    return;
  }
  class Animations {
    /**
     * Initializes a new instance of the class.
     *
     * @constructor
     * @property {EventStore} documentVisibleHandler - Handles document visibility events.
     * @property {number} fixedTimeStep - The fixed time step for animations (in milliseconds).
     * @property {?number} lastTimeStamp - The timestamp of the last animation frame.
     * @property {number} accumulatedTime - The accumulated time since the last update.
     * @property {number} animationId - The ID of the current animation frame.
     * @property {Object} eventsToObserve - Object mapping event names to their handlers.
     */
    constructor() {
      this.documentVisibleHandler = new global.NewHomeSource.Carousel.EventStore();
      this.fixedTimeStep = 1000 / 60;
      this.lastTimeStamp = null;
      this.accumulatedTime = 0;
      this.animationId = 0;
      this.eventsToObserve = {
        startAnimation: this.start.bind(this),
      };
    }

    /**
     * Initializes the animation module with the given state.
     * Registers observers for specified events and sets up a handler to reset the animation
     * when the document visibility changes (e.g., when the tab is hidden).
     *
     * @param {Object} state - The state object to be used and observed.
     */
    initialize(state) {
      this.state = state;
      Object.keys(this.eventsToObserve).forEach((event) => this.state.addObserver(event, this));
      this.documentVisibleHandler.add(document, 'visibilitychange', () => {
        if (document.hidden) {
          this.reset();
        }
      });
    }

    /**
     * Handles the document visibility change event.
     * If the document becomes hidden, resets the carousel state.
     *
     * @returns {void}
     */
    onDocumentVisibleChange() {
      if (document.hidden) {
        this.reset();
      }
    }

    /**
     * Cleans up resources used by the carousel animation.
     * Stops the animation and clears the document visibility handler.
     */
    destroy() {
      this.stop();
      this.documentVisibleHandler.clear();
      Object.keys(this.eventsToObserve).forEach((event) => this.state.removeObserver(event, this));
    }

    /**
     * Handles the animation loop for the carousel, updating and rendering animation frames.
     * Uses a fixed time step for updates and interpolates rendering with an alpha value.
     * Notifies observers of 'updateAnimation' and 'renderAnimation' events.
     *
     * @param {DOMHighResTimeStamp} timeStamp - The current time provided by requestAnimationFrame.
     */
    animate(timeStamp) {
      if (!this.animationId) {
        return;
      }

      if (!this.lastTimeStamp) {
        this.lastTimeStamp = timeStamp;
        this.state.notifyObservers('updateAnimation');
        this.state.notifyObservers('updateAnimation');
      }
      const timeElapsed = timeStamp - this.lastTimeStamp;
      this.lastTimeStamp = timeStamp;
      this.accumulatedTime += timeElapsed;
      while (this.accumulatedTime >= this.fixedTimeStep) {
        this.state.notifyObservers('updateAnimation');
        this.accumulatedTime -= this.fixedTimeStep;
      }
      const alpha = this.accumulatedTime / this.fixedTimeStep;
      this.state.notifyObservers('renderAnimation', Object.assign({}, { alpha }));

      if (this.animationId) {
        this.animationId = window.requestAnimationFrame(this.animate.bind(this));
      }
    }

    /**
     * Starts the animation loop if it is not already running.
     * Uses `requestAnimationFrame` to schedule the next animation frame.
     * Prevents multiple animation loops from being started simultaneously.
     */
    start() {
      if (this.animationId) {
        return;
      }

      this.animationId = window.requestAnimationFrame(this.animate.bind(this));
    }

    /**
     * Stops the current animation by cancelling the animation frame,
     * resetting the timestamp, accumulated time, and animation ID.
     */
    stop() {
      window.cancelAnimationFrame(this.animationId);
      this.lastTimeStamp = null;
      this.accumulatedTime = 0;
      this.animationId = 0;
    }

    /**
     * Resets the animation state by clearing the last timestamp and accumulated time.
     * Typically used to restart the animation from its initial state.
     */
    reset() {
      this.lastTimeStamp = null;
      this.accumulatedTime = 0;
    }

    /**
     * Updates the state by invoking the callback associated with the given topic.
     *
     * @param {object} data - The data to pass to the callback function.
     * @param {string} topic - The topic whose callback should be invoked.
     */
    update(data, topic) {
      const callback = this.eventsToObserve[topic];
      if (callback) {
        callback(data);
      }
    }
  }
  global.NewHomeSource.Carousel.Animations = Animations;
}(window));
